import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../Firebase/firebase-config";
import { useAuth } from "./AuthContext";

/**
 * CartContext : Contexte global pour gérer le panier
 * - Écoute en temps réel la collection Firestore "panier"
 * - Fournit cartCount, cartItems, et le mini-panier coulissant (drawer)
 */
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { uid, authReady } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);

  const openCartDrawer = useCallback(() => setIsCartDrawerOpen(true), []);
  const closeCartDrawer = useCallback(() => setIsCartDrawerOpen(false), []);

  useEffect(() => {
    if (!authReady || !uid) {
      setCartCount(0);
      setCartItems([]);
      return undefined;
    }

    const panierQ = query(collection(db, "panier"), where("userId", "==", uid));

    const unsubscribe = onSnapshot(panierQ, (snapshot) => {
      let total = 0;
      const items = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const qty = data.quantity ?? 1;
        total += qty;
        return {
          id: data.id ?? docSnap.id,
          firestoreId: docSnap.id,
          slug: data.slug,
          name: data.name,
          category: data.category,
          price: data.price,
          image: data.image,
          stripePriceId: data.stripePriceId,
          quantity: qty,
        };
      });

      setCartCount(total);
      setCartItems(items);
    });

    return () => unsubscribe();
  }, [authReady, uid]);

  return (
    <CartContext.Provider
      value={{
        cartCount,
        cartItems,
        isCartDrawerOpen,
        openCartDrawer,
        closeCartDrawer,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

/**
 * Hook pour utiliser le contexte panier (compteur + drawer)
 */
export function useCartCount() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCartCount doit être utilisé à l'intérieur d'un CartProvider");
  }
  return context;
}

/**
 * Hook pour accéder au drawer du panier
 */
export function useCartDrawer() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCartDrawer doit être utilisé à l'intérieur d'un CartProvider");
  }
  return {
    isOpen: context.isCartDrawerOpen,
    open: context.openCartDrawer,
    close: context.closeCartDrawer,
  };
}

